// initGallery.js
export default function initGallery(userOpts = {}) {
  const opts = {
    root: document,

    // ORIGINAL defaults (keeps your current behavior)
    triggerSelector: "article.tag-hash-gallery",
    contentSelector: ".edition-content",

    // REUSE mode (optional)
    scopeSelector: null,          // e.g. ".is-gallery"
    scopeContentSelector: null,   // e.g. ".accordion-content" (inside each scope)

    // ✅ WHERE to insert gallery (slot)
    // Can exist inside scope OR trigger OR content (in that order)
    insertSelector: null,         // e.g. ".edition-gallery-target" or ".gallery-target"
    insert: "prepend",            // "prepend" | "append"

    figureSelector: "figure.kg-image-card, figure.kg-card.kg-image-card",
    minImages: 2,

    autoplay: true,
    autoplayMs: 2800,
    pauseAfterScrollMs: 1200,

    debug: false,
    ...userOpts,
  };

  const log = (...args) => opts.debug && console.log("[gallery]", ...args);

  const q = (sel, root = opts.root) => root.querySelector(sel);
  const qa = (sel, root = opts.root) => Array.from(root.querySelectorAll(sel));

  function getLargestFromSrcset(img) {
    const srcset = img.getAttribute("srcset");
    if (!srcset) return img.getAttribute("src") || img.src;
    const parts = srcset.split(",").map(s => s.trim()).filter(Boolean);
    const last = parts[parts.length - 1];
    return last.split(/\s+/)[0] || (img.getAttribute("src") || img.src);
  }

  // ---------- lightbox ----------
  function buildLightbox() {
    let el = q(".edition-lightbox");
    if (el) return el;

    el = document.createElement("div");
    el.className = "edition-lightbox";
    el.innerHTML = `
      <div class="edition-lightbox-backdrop" data-close="1"></div>
      <div class="edition-lightbox-panel" role="dialog" aria-modal="true" aria-label="Image viewer">
        <button class="edition-lightbox-close" type="button" aria-label="Close" data-close="1">✕</button>
        <button class="edition-lightbox-prev" type="button" aria-label="Previous">‹</button>
        <button class="edition-lightbox-next" type="button" aria-label="Next">›</button>

        <div class="edition-lightbox-media">
          <div class="edition-lightbox-loader"><div class="edition-spinner"></div></div>
          <img alt="">
        </div>

        <div class="edition-lightbox-caption"></div>
      </div>
    `;
    document.body.appendChild(el);
    return el;
  }

  function lockScroll() {
    const y = window.scrollY || 0;
    document.documentElement.dataset.lbScrollY = String(y);

    document.body.style.position = "fixed";
    document.body.style.top = `-${y}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
  }

  function unlockScroll() {
    const y = Number(document.documentElement.dataset.lbScrollY || "0");

    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.width = "";

    window.scrollTo(0, y);
    delete document.documentElement.dataset.lbScrollY;
  }

  function safeInsert(target, node) {
    if (!target) return;
    if (opts.insert === "append") target.appendChild(node);
    else target.prepend(node);
  }

  // ✅ NEW: resolve insert target using scope -> trigger -> content
  function resolveInsertTarget({ scopeEl, triggerEl, contentEl }) {
    if (!contentEl) return null;

    // no custom slot? insert into content (as before)
    if (!opts.insertSelector) return contentEl;

    // 1) inside scope (best for reusable blocks)
    if (scopeEl) {
      const t = scopeEl.querySelector(opts.insertSelector);
      if (t) return t;
    }

    // 2) inside trigger (best for original edition article)
    if (triggerEl) {
      const t = triggerEl.querySelector(opts.insertSelector);
      if (t) return t;
    }

    // 3) inside content (fallback)
    const t = contentEl.querySelector(opts.insertSelector);
    if (t) return t;

    // 4) fallback
    return contentEl;
  }

  // ---------- build gallery in ONE container ----------
  function buildGallery({ scopeEl, triggerEl, contentEl }) {
    if (!contentEl) return;

    // Avoid double-init per container
    if (contentEl.dataset.galleryInited === "1") return;

    const figures = qa(opts.figureSelector, contentEl)
      .filter(fig => fig.querySelector("img.kg-image, img"));

    if (figures.length < opts.minImages) return;

    // mark now (prevents race/double init)
    contentEl.dataset.galleryInited = "1";

    const frame = document.createElement("div");
    frame.className = "edition-gallery-frame";

    const inner = document.createElement("div");
    inner.className = "edition-gallery-inner";

    const gallery = document.createElement("section");
    gallery.className = "edition-gallery";

    const nav = document.createElement("div");
    nav.className = "edition-gallery-nav";
    nav.innerHTML = `
      <button class="edition-gallery-btn" type="button" data-dir="-1" aria-label="Scroll left">‹</button>
      <button class="edition-gallery-btn" type="button" data-dir="1" aria-label="Scroll right">›</button>
    `;

    const track = document.createElement("div");
    track.className = "edition-gallery-track";

    figures.forEach((fig, idx) => {
      fig.classList.add("edition-gallery-item");
      fig.dataset.galleryIndex = String(idx);

      const img = fig.querySelector("img.kg-image, img");
      if (img) {
        img.loading = "lazy";
        img.decoding = "async";
        img.dataset.fullsrc = getLargestFromSrcset(img);
      }

      track.appendChild(fig);
    });

    gallery.appendChild(nav);
    gallery.appendChild(track);
    inner.appendChild(gallery);
    frame.appendChild(inner);

    // ✅ insert at your chosen slot
    const insertTarget = resolveInsertTarget({ scopeEl, triggerEl, contentEl });
    safeInsert(insertTarget, frame);

    // ----- step -----
    function cardStep() {
      const first = track.querySelector(".edition-gallery-item");
      if (!first) return 600;
      const gap = parseFloat(getComputedStyle(track).gap || "18");
      return first.getBoundingClientRect().width + gap;
    }

    nav.addEventListener("click", (e) => {
      const btn = e.target.closest("button[data-dir]");
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      const dir = Number(btn.dataset.dir || "0");
      track.scrollBy({ left: dir * cardStep(), behavior: "smooth" });
    });

    // ----- autoplay -----
    let timer = null;
    let paused = false;
    let pauseTO = null;

    function stopAutoplay() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    function startAutoplay() {
      if (!opts.autoplay) return;
      stopAutoplay();
      timer = setInterval(() => {
        if (paused) return;

        const step = cardStep();
        const maxScroll = track.scrollWidth - track.clientWidth;

        if (track.scrollLeft >= maxScroll - 8) {
          track.scrollTo({ left: 0, behavior: "smooth" });
        } else {
          track.scrollBy({ left: step, behavior: "smooth" });
        }
      }, opts.autoplayMs);
    }

    track.addEventListener("mouseenter", () => (paused = true));
    track.addEventListener("mouseleave", () => (paused = false));
    track.addEventListener("touchstart", () => (paused = true), { passive: true });
    track.addEventListener("touchend", () => (paused = false), { passive: true });

    track.addEventListener("scroll", () => {
      paused = true;
      clearTimeout(pauseTO);
      pauseTO = setTimeout(() => (paused = false), opts.pauseAfterScrollMs);
    }, { passive: true });

    startAutoplay();

    // ----- lightbox -----
    const lightbox = buildLightbox();
    const panel = lightbox.querySelector(".edition-lightbox-panel");
    const lbImg = lightbox.querySelector(".edition-lightbox-media img");
    const lbCaption = lightbox.querySelector(".edition-lightbox-caption");
    const btnPrev = lightbox.querySelector(".edition-lightbox-prev");
    const btnNext = lightbox.querySelector(".edition-lightbox-next");
    const btnClose = lightbox.querySelector(".edition-lightbox-close");

    let currentIndex = 0;

    function setLoading(on) {
      lightbox.classList.toggle("is-loading", !!on);
    }

    function show(src, alt) {
      setLoading(true);
      lbImg.classList.remove("is-loaded");

      const pre = new Image();
      pre.decoding = "async";
      pre.onload = () => {
        lbImg.src = src;
        lbImg.alt = alt || "";
        requestAnimationFrame(() => {
          lbImg.classList.add("is-loaded");
          setLoading(false);
        });
      };
      pre.onerror = () => setLoading(false);
      pre.src = src;
    }

    function openAt(index) {
      const items = Array.from(track.querySelectorAll("figure.edition-gallery-item"));
      if (!items.length) return;

      const alreadyOpen = lightbox.classList.contains("is-open");

      currentIndex = (index + items.length) % items.length;
      const fig = items[currentIndex];
      const img = fig.querySelector("img.kg-image, img");
      const cap = fig.querySelector("figcaption");

      if (!alreadyOpen) {
        lightbox.classList.add("is-open");
        lockScroll();
        stopAutoplay();
      }

      lbCaption.textContent = cap ? cap.textContent.trim() : "";
      const src = img?.dataset.fullsrc || img?.currentSrc || img?.src || "";
      show(src, img?.alt || "");
    }

    function close() {
      lightbox.classList.remove("is-open");
      lbImg.src = "";
      lbImg.classList.remove("is-loaded");
      setLoading(false);
      unlockScroll();
      startAutoplay();
    }

    function next() { openAt(currentIndex + 1); }
    function prev() { openAt(currentIndex - 1); }

    track.addEventListener("click", (e) => {
      const img = e.target.closest("img");
      if (!img) return;
      const fig = img.closest("figure.edition-gallery-item");
      if (!fig) return;
      openAt(Number(fig.dataset.galleryIndex || "0"));
    });

    panel.addEventListener("click", (e) => e.stopPropagation());

    lightbox.addEventListener("click", (e) => {
      if (e.target?.dataset?.close === "1") close();
      if (e.target === lbImg) close();
    });

    btnNext.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      next();
    });

    btnPrev.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      prev();
    });

    btnClose?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      close();
    });

    window.addEventListener("keydown", (e) => {
      if (!lightbox.classList.contains("is-open")) return;
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    });

    log("Gallery built. Inserted into:", insertTarget);
  }

  // ---------- init (supports original + reusable) ----------
  function init() {
    // MODE A: reusable scopes (multiple)
    if (opts.scopeSelector) {
      const scopes = qa(opts.scopeSelector);
      if (!scopes.length) {
        log("No scopes found:", opts.scopeSelector);
        return;
      }

      scopes.forEach(scopeEl => {
        const contentEl = opts.scopeContentSelector
          ? scopeEl.querySelector(opts.scopeContentSelector)
          : scopeEl;

        buildGallery({ scopeEl, triggerEl: scopeEl, contentEl });
      });

      return;
    }

    // MODE B: original (single trigger + single content)
    const triggerEl = q(opts.triggerSelector);
    if (!triggerEl) {
      log("No trigger found:", opts.triggerSelector);
      return;
    }

    const contentEl = q(opts.contentSelector);
    if (!contentEl) {
      log("No content found:", opts.contentSelector);
      return;
    }

    buildGallery({ scopeEl: null, triggerEl, contentEl });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  return { init };
}

// OPTIONAL global exposure for inline Ghost scripts
if (typeof window !== "undefined") {
  window.initGallery = function (opts) {
    return initGallery(opts);
  };
}